import { useState } from "react";
import { useTranslation } from "react-i18next";
import CommonImages from "@base/assets/images";
import CommonStyles from "@components/CommonStyles";
import { getAvatarLink } from "@base/helpers/common";
import { IDoctor } from "@base/modules/doctor/interface/doctor.interface";
import { DoctorGroup } from "@base/modules/patientSpecialPackage/packageBookingRules";
import DoctorInfoModal from "./DoctorInfoModal";

interface IProps {
  label?: string;
  emptyText?: string;
  isLoading?: boolean;
  groups: DoctorGroup[];
  doctors: IDoctor[];
  selectedGroupCode?: string;
  selectedDoctorCode?: string;
  /** Shown at the right of the label, e.g. the province a home-care doctor covers. */
  hint?: string;
  displayName: (doctor: IDoctor) => string;
  /** Whether a doctor gets the read-only "Details" pill. */
  canViewInfo?: (doctor: IDoctor) => boolean;
  /** Set when the list has more pages than the form shows. */
  onSeeAll?: () => void;
  onSelectGroup: (groupCode: string) => void;
  onSelectDoctor: (doctor: IDoctor) => void;
}

/** The doctor block of a booking form, with the group chips when the doctors come in groups. */
export default function DoctorPicker(props: IProps) {
  //! state
  const {
    label,
    emptyText,
    isLoading,
    groups,
    doctors,
    selectedGroupCode,
    selectedDoctorCode,
    hint,
    displayName,
    canViewInfo,
    onSeeAll,
    onSelectGroup,
    onSelectDoctor,
  } = props;
  const { t } = useTranslation();
  const [viewingDoctor, setViewingDoctor] = useState<IDoctor>();

  //! render
  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-between items-center gap-2">
        <span className="text-common-maintext text-sm font-medium font-['Inter']">
          {label} <span className="text-common-red500">*</span>
        </span>
        <div className="flex items-center gap-3">
          {hint ? <span className="text-common-subtext text-xs font-normal font-['Inter']">{hint}</span> : null}
          {onSeeAll ? (
            <button type="button" className="text-common-primary text-sm font-medium font-['Inter']" onClick={onSeeAll}>
              {t("booking.seeAll")}
            </button>
          ) : null}
        </div>
      </div>

      {/* Shown even for a single group: picking it is how the patient says where the service happens. */}
      {groups.length ? (
        <div className="flex flex-wrap items-center gap-2">
          {groups.map((group) => {
            const isActive = group.code === selectedGroupCode;

            return (
              <button
                type="button"
                key={group.code}
                onClick={() => onSelectGroup(group.code)}
                className={`px-3 py-2 rounded-lg border text-sm font-medium font-['Inter'] ${
                  isActive
                    ? "border-common-primary bg-common-primary100 text-common-primary"
                    : "border-common-divider bg-common-white text-common-maintext"
                }`}
              >
                {group.name}
              </button>
            );
          })}
        </div>
      ) : null}

      {isLoading ? <CommonStyles.LoadingUI /> : null}

      {!isLoading && !doctors.length ? (
        <div className="w-full bg-common-background rounded-lg p-3 text-center text-common-subtext text-sm font-normal font-['Inter']">
          {emptyText}
        </div>
      ) : null}

      {!isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
          {doctors.map((doctor) => {
            const isActive = doctor.doctor_code === selectedDoctorCode;

            return (
              <div
                key={doctor.doctor_code}
                onClick={() => onSelectDoctor(doctor)}
                className={`flex flex-col items-center gap-2 p-3 rounded-lg border cursor-pointer ${
                  isActive ? "border-common-primary bg-common-primary100" : "border-common-divider bg-common-white"
                }`}
              >
                <CommonStyles.ImageUI
                  className="rounded-full object-cover"
                  width={56}
                  height={56}
                  preview={false}
                  // Only the `avatar` entry: `thumbnail` is a crop that looks wrong at this size.
                  src={getAvatarLink(doctor.avatar ?? [])}
                  fallback={CommonImages.DrafDoctor}
                />

                <CommonStyles.TypographyUI
                  use="paragraph"
                  className="text-common-maintext text-sm font-medium font-['Inter'] !mb-0 text-center line-clamp-2"
                  title={displayName(doctor)}
                >
                  {displayName(doctor)}
                </CommonStyles.TypographyUI>

                {canViewInfo?.(doctor) ? (
                  <CommonStyles.ButtonUI
                    size="small"
                    ghost
                    type="primary"
                    onClick={(event) => {
                      // The card itself selects the doctor, so the pill must not do both.
                      event.stopPropagation();
                      setViewingDoctor(doctor);
                    }}
                  >
                    {t("booking.doctorDetail")}
                  </CommonStyles.ButtonUI>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}

      {viewingDoctor ? <DoctorInfoModal open toggle={() => setViewingDoctor(undefined)} doctor={viewingDoctor} /> : null}
    </div>
  );
}
